<template>
  <div class="smart-lodging-container theme-transition">
    <div class="lodging-header card-base theme-transition mb-3">
      <div class="row p-3">
        <div class="col-md-4">
          <label for="search">Search Rooms</label>
          <input
            type="text"
            class="form-control"
            v-model="searchQuery"
            id="search"
            placeholder="Search by room number or guest name"
          />
        </div>
        <div class="col-md-4">
          <label for="statusFilter">Status</label>
          <select class="form-select" v-model="statusFilter" id="statusFilter">
            <option value="">All Statuses</option>
            <option value="CHECKED_IN">Checked In</option>
            <option value="CHECKED_OUT">Checked Out</option>
            <option value="RESERVED">Reserved</option>
          </select>
        </div>
        <div class="col-md-4 d-flex align-items-end">
          <button @click="refreshRooms" class="btn btn-primary w-100">
            <font-awesome-icon icon="fa-solid fa-sync" :class="{ 'fa-spin': loading }" />
            Refresh
          </button>
        </div>
      </div>
    </div>

    <div class="rooms-grid">
      <div
        v-for="room in filteredRooms"
        :key="room.roomId"
        class="room-card card-base theme-transition"
        :class="{ 'room-occupied': room.status === 'CHECKED_IN' }"
      >
        <div class="room-header">
          <div class="room-number">{{ room.roomNumber }}</div>
          <div class="room-status" :class="`status-${room.status.toLowerCase()}`">
            {{ formatStatus(room.status) }}
          </div>
        </div>

        <div class="room-details">
          <div class="detail-row">
            <font-awesome-icon icon="fa-solid fa-user" class="detail-icon" />
            <span>{{ room.guestName }}</span>
          </div>
          <div class="detail-row">
            <font-awesome-icon icon="fa-solid fa-calendar" class="detail-icon" />
            <span>
              In: {{ formatDate(room.checkInDate) }} |
              Out: {{ formatDate(room.checkOutDate) }}
            </span>
          </div>
          <div class="detail-row">
            <font-awesome-icon icon="fa-solid fa-clock" class="detail-icon" />
            <span>Guests: {{ room.numberOfGuests }}</span>
          </div>
          <div class="detail-row" v-if="room.balanceDue !== undefined || room.totalAmount">
            <font-awesome-icon icon="fa-solid fa-money-bill-wave" class="detail-icon" />
            <div class="amount-info">
              <div v-if="room.balanceDue !== undefined && room.balanceDue > 0" class="amount due">
                <span class="label">Balance Due:</span>
                <span class="value">{{ $utils.formatMoney(room.balanceDue) }}</span>
              </div>
              <div v-if="room.totalAmount" class="amount total">
                <span class="label">Total:</span>
                <span class="value">{{ $utils.formatMoney(room.totalAmount) }}</span>
              </div>
            </div>
          </div>
          <div class="detail-row" v-if="room.preferredPaymentMethod">
            <font-awesome-icon icon="fa-solid fa-credit-card" class="detail-icon" />
            <span>Payment: {{ room.preferredPaymentMethod }}</span>
          </div>
        </div>

        <div class="room-actions">

          <button
            v-if="room.status === 'CHECKED_IN'"
            @click="openBillDetails(room)"
            class="btn btn-outline-info btn-sm"
          >
            <font-awesome-icon icon="fa-solid fa-receipt" />
            View Bill
          </button>
          <button
            v-if="room.status === 'CHECKED_IN' && (room.balanceDue > 0 || room.totalAmount > 0)"
            @click="openPaymentModal(room)"
            class="btn btn-success btn-sm"
          >
            <font-awesome-icon icon="fa-solid fa-dollar-sign" />
            Make Payment
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading && rooms.length === 0" class="loading-state">
      <div class="spinner"></div>
      <p>Loading rooms...</p>
    </div>

    <div v-if="!loading && filteredRooms.length === 0" class="empty-state">
      <font-awesome-icon icon="fa-solid fa-bed" class="empty-icon" />
      <p>No rooms found</p>
    </div>



    <RoomBillModal
      :show="showBillModal"
      :room="selectedRoom"
      @close="showBillModal = false"
      @process-payment="openPaymentModal"
    />

    <PaymentModal
      :show="showPaymentModal"
      :room="selectedRoom"
      @close="showPaymentModal = false"
      @payment-complete="onPaymentComplete"
    />
  </div>
</template>

<script>
import LodgingIntegration from '@/services/lodgingIntegration';

import RoomBillModal from './RoomBillModal.vue';
import PaymentModal from './PaymentModal.vue';

export default {
  name: 'SmartLodging',
  components: {

    RoomBillModal,
    PaymentModal
  },
  data() {
    return {
      rooms: [],
      loading: false,
      error: null,
      searchQuery: '',
      statusFilter: '',
      selectedRoom: null,

      showBillModal: false,
      showPaymentModal: false
    };
  },
  computed: {
    filteredRooms() {
      let filtered = this.rooms;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(room =>
          room.roomNumber.toLowerCase().includes(query) ||
          room.guestName.toLowerCase().includes(query)
        );
      }

      if (this.statusFilter) {
        filtered = filtered.filter(room => room.status === this.statusFilter);
      }

      return filtered;
    }
  },
  async mounted() {
    await this.loadRooms();
  },
  methods: {
    async loadRooms() {
      this.loading = true;
      this.error = null;

      try {
        this.rooms = (await LodgingIntegration.getAllRooms()).map(room => ({
          ...room,
          status: room.status || 'CHECKED_IN',
          // Calculate balance if not provided
          balanceDue: room.balanceDue || (room.totalAmount && room.totalPaid
            ? room.totalAmount - room.totalPaid
            : room.totalAmount) || 0
        }));
      } catch (error) {
        console.error('Failed to load rooms:', error);
        this.error = error.message || 'Failed to load rooms';
        Swal.fire({
          title: 'Error',
          text: this.error,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        this.loading = false;
      }
    },

    async refreshRooms() {
      await this.loadRooms();
    },



    async openBillDetails(room) {
      this.loading = true;
      try {
        // Fetch detailed booking information
        const bookingId = room.bookingId || room.id;
        const detailedRoom = await LodgingIntegration.getBookingDetails(bookingId);

        // Merge the detailed information with the room data
        this.selectedRoom = {
          ...room,
          ...detailedRoom,
          // Ensure financial fields are available
          totalAmount: detailedRoom.totalAmount || room.totalAmount || 0,
          balanceDue: detailedRoom.balanceDue || room.balanceDue || 0,
          roomCharges: detailedRoom.roomCharges,
          serviceCharges: detailedRoom.serviceCharges,
          minifridgeCharges: detailedRoom.minifridgeCharges,
          taxAmount: detailedRoom.taxAmount,
          totalPaid: detailedRoom.totalPaid,
          guestEmail: detailedRoom.guestEmail,
          guestPhone: detailedRoom.guestPhone
        };
        this.showBillModal = true;
      } catch (error) {
        console.error('Failed to load room details:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to load room details. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        // Fallback to showing the modal with basic room info
        this.selectedRoom = room;
        this.showBillModal = true;
      } finally {
        this.loading = false;
      }
    },

    async openPaymentModal(room) {
      this.loading = true;
      this.selectedRoom = room; // Set immediately to avoid undefined

      try {
        // Fetch detailed booking information to get current balance
        const bookingId = room.bookingId || room.id;
        const detailedRoom = await LodgingIntegration.getBookingDetails(bookingId);

        // Merge the detailed information with the room data
        this.selectedRoom = {
          ...room,
          ...detailedRoom,
          // Ensure financial fields are available
          totalAmount: detailedRoom.totalAmount || room.totalAmount || 0,
          balanceDue: detailedRoom.balanceDue || room.balanceDue || (detailedRoom.totalAmount - detailedRoom.totalPaid) || 0,
          roomCharges: detailedRoom.roomCharges,
          serviceCharges: detailedRoom.serviceCharges,
          minifridgeCharges: detailedRoom.minifridgeCharges,
          taxAmount: detailedRoom.taxAmount,
          totalPaid: detailedRoom.totalPaid,
          guestEmail: detailedRoom.guestEmail,
          guestPhone: detailedRoom.guestPhone
        };
      } catch (error) {
        console.error('Failed to load payment details:', error);
        // Still open the modal with basic room info
        this.selectedRoom = {
          ...room,
          totalAmount: room.totalAmount || 0,
          balanceDue: room.balanceDue || 0
        };

        // Show a warning but don't block the payment
        Swal.fire({
          title: 'Warning',
          text: 'Using cached room data. Some details may not be up to date.',
          icon: 'warning',
          confirmButtonText: 'OK',
          timer: 3000
        });
      } finally {
        this.showPaymentModal = true; // Always open the modal
        this.loading = false;
      }
    },

    onPaymentComplete(paymentData) {
      this.showPaymentModal = false;
      Swal.fire({
        title: 'Success',
        text: 'Payment processed successfully',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
      this.refreshRooms();
    },

    formatStatus(status) {
      return status.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.smart-lodging-container {
  padding: 20px;
  min-height: 100vh;
}

.lodging-header {
  padding: 0;
}

.rooms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.room-card {
  padding: 20px;
  border-radius: 12px;
  transition: var(--transition-speed);
  border: 2px solid var(--card-border);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--card-shadow-hover);
  }

  &.room-occupied {
    border-color: var(--accent-electric-blue);
  }
}

.room-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--card-border);

  .room-number {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--accent-electric-blue);
  }

  .room-status {
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;

    &.status-checked_in {
      background: rgba(40, 167, 69, 0.2);
      color: #28a745;
    }

    &.status-checked_out {
      background: rgba(108, 117, 125, 0.2);
      color: #6c757d;
    }

    &.status-reserved {
      background: rgba(255, 193, 7, 0.2);
      color: #ffc107;
    }
  }
}

.room-details {
  margin-bottom: 15px;

  .detail-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    color: var(--text-primary);

    .detail-icon {
      width: 20px;
      margin-right: 10px;
      color: var(--accent-electric-blue);
    }

    .amount-info {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .amount {
        display: flex;
        align-items: center;
        gap: 8px;

        .label {
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .value {
          font-weight: 600;
        }

        &.due .value {
          color: var(--danger);
        }

        &.total .value {
          color: var(--success);
        }
      }
    }
  }
}

.room-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  button {
    flex: 1;
    min-width: 120px;
  }
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);

  .spinner {
    border: 4px solid var(--card-border);
    border-top: 4px solid var(--accent-electric-blue);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  }

  .empty-icon {
    font-size: 4rem;
    color: var(--accent-electric-blue);
    opacity: 0.3;
    margin-bottom: 20px;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.form-control, .form-select {
  background: var(--card-bg);
  border: var(--card-border);
  color: var(--text-primary);

  &:focus {
    background: var(--card-bg);
    border-color: var(--accent-primary);
    color: var(--text-primary);
    box-shadow: 0 0 0 0.2rem rgba(var(--accent-electric-blue-rgb), 0.15);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
}

label {
  color: var(--text-primary);
}
</style>
